import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useTsrClient } from './tsr'
import { CheckCircleIcon, FolderSyncIcon } from 'lucide-react'
import classNames from 'classnames'
import { format, differenceInYears } from 'date-fns'
import { parseFlexibleDate } from '../../lib/utils'
import { useTranslation } from 'react-i18next' // Import useTranslation

export function UserProfilePage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const tsr = useTsrClient()
  const { t } = useTranslation() // Initialize t function

  const handleBack = () => {
    // Check if there's a state with the previous URL and query params
    if (location.state?.from) {
      navigate(location.state.from)
    } else {
      // Fallback: try to go back in history, or navigate to end-users if no history
      if (window.history.length > 1) {
        navigate(-1)
      } else {
        navigate('/end-users')
      }
    }
  }

  const {
    data: userData,
    isPending,
    error,
  } = tsr.endUser.getById.useQuery({
    queryData: { params: { id: id?.toString() || '' } },
    queryKey: ['endUser', id],
  })

  if (isPending) return <div>{t('loading')}</div>

  if (error)
    return (
      <div>
        {t('error_loading_user')}: {JSON.stringify(error) || t('unknown_error')}
      </div>
    )

  const user = userData?.body
  if (!user) return <div>{t('user_not_found')}</div>

  const detailItemClassName = 'py-2'
  const labelClassName = 'font-semibold text-gray-600'
  const valueClassName = 'text-gray-800'

  return (
    <div className="bg-ui-100 p-8 h-full">
      <div className="p-8 sm:rounded-lg bg-white shadow">
        <div className="w-full flex justify-between items-center pb-6 mb-4 border-b border-gray-200">
          <h1 className="font-bold text-3xl">
            {t('end_users.user_profile.account_info_header')}{' '}
            {[user.firstName, user.middle, user.lastName].filter(Boolean).join(' ')}
          </h1>
          <button
            onClick={handleBack}
            className="px-4 py-2 text-sm font-medium text-indigo-600 bg-white border border-indigo-600 rounded-md hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
          >
            {t('back')}
          </button>
        </div>
        <div className="">
          {/* User Details Section */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-700 mb-4 border-b pb-2">
              {t('end_users.user_profile.personal_details_header')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
              <div className={detailItemClassName}>
                <span className={labelClassName}>
                  {t('end_users.user_profile.full_name_label')}:{' '}
                </span>
                <span className={valueClassName}>{[user.firstName, user.middle, user.lastName].filter(Boolean).join(' ')}</span>
              </div>
              <div className={detailItemClassName}>
                <span className={labelClassName}>
                  {t('end_users.user_profile.id_label')}:{' '}
                </span>
                <span className={valueClassName}>{user.id}</span>
              </div>
              <div className={detailItemClassName}>
                <span className={labelClassName}>
                  {t('end_users.user_profile.email_label')}:{' '}
                </span>
                <span className={valueClassName}>{user.email || t('na')}</span>
              </div>
              <div className={detailItemClassName + ' md:col-span-2'}>
                <span className={labelClassName}>
                  {t('end_users.user_profile.address_label')}:{' '}
                </span>
                <span className={valueClassName}>
                  {user.address
                    ? typeof user.address === 'object'
                      ? `${user.address.streetName || ''} ${user.address.streetNumber || ''
                        }, ${user.address.city || ''}, ${user.address.country || ''
                        }`.replace(/ , |, $/g, '') || t('na')
                      : user.address
                    : t('na')}
                </span>
              </div>
            </div>
          </section>

          {/* Campaigns Section */}
          {user.endUserCampaigns && user.endUserCampaigns.length > 0 && (
            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-6 border-b pb-3">
                {t('end_users.user_profile.campaigns_header')}
              </h2>
              {user.endUserCampaigns.map((userCampaign, index) => {
                if (!userCampaign) return null // Add this check
                const birthday =
                  userCampaign?.birthday &&
                  parseFlexibleDate(userCampaign.birthday)
                return (
                  <div
                    key={userCampaign.campaign.id || index} // Corrected key
                    className="mb-8 p-6 border border-gray-200 rounded-lg shadow-sm bg-gray-50"
                  >
                    <h3 className="text-xl font-semibold text-indigo-700 mb-4">
                      {userCampaign?.campaign.name}
                    </h3>

                    {/* Campaign General Info */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 mb-4">
                      <div className={detailItemClassName}>
                        <span className={labelClassName}>
                          {t('end_users.user_profile.phone_label')}:{' '}
                        </span>
                        <span className={valueClassName}>
                          {userCampaign?.phoneNumber || t('na')}
                        </span>
                      </div>
                      <div className={detailItemClassName}>
                        <span className={labelClassName}>
                          {t('end_users.user_profile.birthday_label')}:{' '}
                        </span>
                        <span className={valueClassName}>
                          {birthday
                            ? `${format(birthday, 'yyyy-MM-dd')} (${t(
                              'end_users.user_profile.years_old',
                              {
                                age: differenceInYears(new Date(), birthday),
                              },
                            )})`
                            : t('na')}
                        </span>
                      </div>
                      <div className={detailItemClassName}>
                        <span className={labelClassName}>
                          {t('end_users.user_profile.digital_code_label')}:{' '}
                        </span>
                        <span className={valueClassName}>
                          {userCampaign?.digitalCode || t('na')}
                        </span>
                      </div>
                      <div className={detailItemClassName}>
                        <span className={labelClassName}>
                          {t(
                            'end_users.user_profile.privacy_link_accepted_label',
                          )}
                          :{' '}
                        </span>
                        <span className={valueClassName}>
                          {userCampaign?.privacyLinkAccepted
                            ? `${t('yes')} ${t('at')} ${format(
                              new Date(userCampaign?.privacyLinkAccepted),
                              'yyyy-MM-dd HH:mm',
                            )}`
                            : t('na')}
                        </span>
                      </div>
                      <div className={detailItemClassName}>
                        <span className={labelClassName}>
                          {t(
                            'end_users.user_profile.allow_contact_consent_label',
                          )}
                          :{' '}
                        </span>
                        <span className={valueClassName}>
                          {userCampaign?.allowContactConsent
                            ? t('yes')
                            : t('no')}
                        </span>
                      </div>
                    </div>

                    {/* Optional Data & Mapping - kept as is for now, can be improved if structure is known */}
                    {userCampaign?.optionalData &&
                      Object.keys(userCampaign.optionalData).length > 0 && (
                        <div className="mb-4">
                          <h4 className="text-md font-semibold text-gray-600 mb-1">
                            {t('end_users.user_profile.optional_data_header')}:
                          </h4>
                          <pre className="bg-gray-100 p-2 rounded text-xs overflow-x-auto">
                            {JSON.stringify(userCampaign.optionalData, null, 2)}
                          </pre>
                        </div>
                      )}
                    {userCampaign?.mapping &&
                      Object.keys(userCampaign.mapping).length > 0 && (
                        <div className="mb-4">
                          <h4 className="text-md font-semibold text-gray-600 mb-1">
                            {t('end_users.user_profile.mapping_header')}:
                          </h4>
                          <pre className="bg-gray-100 p-2 rounded text-xs overflow-x-auto">
                            {JSON.stringify(userCampaign.mapping, null, 2)}
                          </pre>
                        </div>
                      )}

                    {/* Questionnaire Answers */}
                    {userCampaign?.questionnaireAnswers &&
                      Object.keys(userCampaign.questionnaireAnswers).length >
                      0 && (
                        <div className="mb-4">
                          <h4 className="text-md font-semibold text-gray-600 mb-2 pt-2 border-t">
                            {t(
                              'end_users.user_profile.questionnaire_answers_header',
                            )}
                          </h4>
                          <ul className="list-disc list-inside pl-4 space-y-1 text-sm">
                            {Object.entries(
                              userCampaign.questionnaireAnswers,
                            ).map(([key, v]) => (
                              <li key={key}>
                                <span className="font-medium">{key}:</span>{' '}
                                {v?.toString() || t('na')}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                    {/* Happiness Check Answer */}
                    {userCampaign?.happinessAnswers && (
                      <div className="mb-4">
                        <h4 className="text-md font-semibold text-gray-600 mb-2 pt-2 border-t">
                          {t('end_users.user_profile.happiness_check_header')}
                        </h4>
                        <pre className="bg-gray-100 p-2 rounded text-xs overflow-x-auto">
                          {JSON.stringify(
                            userCampaign.happinessAnswers,
                            null,
                            2,
                          )}
                        </pre>
                      </div>
                    )}

                    {/* Incentive */}
                    {userCampaign?.incentive?.incentiveItem && (
                      <div className="mb-4">
                        <h4 className="text-md font-semibold text-gray-600 mb-2 pt-2 border-t">
                          {t('end_users.user_profile.incentive_header')}
                        </h4>
                        <p className="text-sm">
                          <span className="font-medium">
                            {t('end_users.user_profile.incentive_name_label')}:
                          </span>{' '}
                          {userCampaign.incentive.incentiveItem.name}
                        </p>
                      </div>
                    )}

                    {/* Events */}
                    {userCampaign?.events && userCampaign.events.length > 0 && (
                      <div>
                        <h4 className="text-md font-semibold text-gray-600 mb-3 pt-2 border-t">
                          {t('end_users.user_profile.events_header')}
                        </h4>
                        <ul role="list" className="space-y-4">
                          {userCampaign.events
                            .sort(
                              (a, b) =>
                                new Date(a.createdAt).getTime() -
                                new Date(b.createdAt).getTime(),
                            )
                            .map((activityItem, activityItemIdx) => (
                              <li
                                key={activityItem.id}
                                className="relative flex gap-x-3 items-start"
                              >
                                <div
                                  className={classNames(
                                    activityItemIdx ===
                                      (userCampaign.events?.length ?? 0) - 1
                                      ? 'h-6' // Adjusted for icon size
                                      : '-bottom-4', // Adjusted for spacing
                                    'absolute top-0 left-3 flex w-6 justify-center', // Centered icon
                                  )}
                                >
                                  <div className="w-px bg-gray-300" />
                                </div>
                                <div className="relative flex size-6 flex-none items-center justify-center bg-white ring-1 ring-gray-300 rounded-full">
                                  {[
                                    'scheduleOneCommunitySync',
                                    'syncOneCommunity',
                                    'syncOneCommunitySuccess',
                                  ].includes(activityItem.action) ? (
                                    <FolderSyncIcon
                                      aria-hidden="true"
                                      className="size-3.5 text-indigo-600"
                                    />
                                  ) : (
                                    <CheckCircleIcon
                                      aria-hidden="true"
                                      className="size-4 text-green-600"
                                    />
                                  )}
                                </div>
                                <div className="flex-auto">
                                  <p className="text-xs text-gray-700">
                                    <span className="font-medium text-gray-900">
                                      {activityItem.action}
                                    </span>
                                    {activityItem.data &&
                                      Object.keys(activityItem.data).length >
                                      0 && (
                                        <pre className="mt-1 text-xs bg-gray-100 p-1.5 rounded overflow-x-auto">
                                          {JSON.stringify(
                                            activityItem.data,
                                            null,
                                            2,
                                          )}
                                        </pre>
                                      )}
                                  </p>
                                </div>
                                <time
                                  dateTime={new Date(
                                    activityItem.createdAt,
                                  ).toISOString()}
                                  className="flex-none self-center text-xs text-gray-500"
                                >
                                  {format(
                                    new Date(activityItem.createdAt),
                                    'yyyy-MM-dd HH:mm',
                                  )}
                                </time>
                              </li>
                            ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )
              })}
            </section>
          )}
        </div>
      </div>
    </div>
  )
}
